// =========================================
// Date helpers — fonte unica
// =========================================
//
// `toJsDate` era duplicata in components/index.js, data/scoring.js,
// components/DecisionGate.js e inlineata in 7+ punti di ProducerDetailDrawer.
// Centralizzata qui per:
//   1. Eliminare il rischio Invalid Date sui Firestore Timestamp
//   2. Avere un solo posto da aggiornare se cambia la rappresentazione
//
// I formatter di alto livello (formatDate, formatDateShort, ecc.) restano
// in `components/index.js` perché parte della libreria di presentazione UI.

/** ms in un giorno — usato in molti calcoli di scadenze e giacenza. */
export const MS_PER_DAY = 86_400_000;

/** Coerce Firestore Timestamp | Date | string | number → JS Date (o null). */
export const toJsDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate(); // Firestore Timestamp
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Converte il valore di un <input type="date"> ("YYYY-MM-DD") o
 * <input type="datetime-local"> ("YYYY-MM-DDTHH:mm") in un oggetto Date LOCALE
 * (→ Firestore Timestamp quando salvato). null se vuoto/non valido.
 *
 * NB anti-shift-UTC: `new Date("2026-07-02")` viene interpretato come UTC dal
 * costruttore JS (→ shift di fuso), mentre le date-only le costruiamo esplicitamente
 * come mezzanotte LOCALE con new Date(y, m-1, d). Le datetime-local sono già locali.
 */
export const inputToDate = (str) => {
  if (!str || typeof str !== 'string') return null;
  const s = str.trim();
  if (!s) return null;
  if (s.length <= 10) {
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d);
    return isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(s); // datetime-local → interpretato come ora locale
  return isNaN(dt.getTime()) ? null : dt;
};

/** Timestamp | Date | string → "YYYY-MM-DD" per <input type="date">. '' se null. */
export const toDateInput = (v) => {
  const d = toJsDate(v);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Timestamp | Date | string → "YYYY-MM-DDTHH:mm" per <input type="datetime-local">. '' se null. */
export const toDateTimeInput = (v) => {
  const d = toJsDate(v);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Chiave-giorno locale "YYYY-MM-DD" da Timestamp | Date | ISO string | number.
 * Per le stringhe usa i primi 10 char (già una data ISO). null se non valida.
 * Usata per confronti per-giorno (route/interventi schedulati per data).
 */
export const toLocalDay = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  const d = toJsDate(v);
  if (!d) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
